import { z } from 'zod';
import { deliveryPreferenceSchema } from '@/types/delivery-preference';
import { adapterClient, handleApiError } from './client';
import { canonicalProjectId, getProjectView } from './projectViews';
import { entityIdSchema, u32 } from '@/types/current-project';
import { proposalDefinitionSchema } from '@/types/project-command';

const revision = z.number().int().nonnegative().safe();
const bounded = (bytes: number) =>
  z
    .string()
    .refine(
      (value) =>
        !value.includes('\0') &&
        new TextEncoder().encode(value).length <= bytes,
      'Text exceeds the allowed size.'
    );
export const milestonePresentationSchema = z.object({
  key: u32,
  description: bounded(2000),
  requirements: z
    .array(
      z.object({
        key: u32,
        experience: z.enum(['Junior', 'MidLevel', 'Senior']).nullable(),
      })
    )
    .max(100),
});
export type MilestonePresentation = z.infer<typeof milestonePresentationSchema>;
const presentationSchema = z.object({
  proposalId: entityIdSchema,
  revision,
  proposalRevision: revision,
  matchesCurrentDefinition: z.boolean(),
  milestones: z.array(milestonePresentationSchema).max(100),
});
export type ProposalPresentation = z.infer<typeof presentationSchema>;
export const commentBodySchema = z
  .object({
    requestId: entityIdSchema,
    expectedProposalRevision: revision,
    message: bounded(10000).refine(
      (value) => /\S/.test(value),
      'Enter a comment.'
    ),
  })
  .strict();
const commentSchema = z.object({
  commentId: revision,
  proposalRevision: revision,
  authorAccount: z.string().regex(/^0x[0-9a-f]{64}$/i),
  createdAt: z.number().int().safe(),
  message: z.string(),
  definition: proposalDefinitionSchema,
  delivery: deliveryPreferenceSchema.nullable().default(null),
  presentation: presentationSchema.nullable(),
});
export type ProposalComment = z.infer<typeof commentSchema>;
const commentsSchema = z.object({
  items: z.array(commentSchema).max(20),
  nextAfter: revision.nullable(),
});
const path = (project: string, proposal: string) =>
  '/projects/' +
  canonicalProjectId(project) +
  '/proposals/' +
  canonicalProjectId(proposal);
export async function getProposalPresentation(
  project: string,
  proposal: string,
  signal?: AbortSignal
) {
  try {
    const { data } = await adapterClient.get<unknown>(
      path(project, proposal) + '/presentation',
      { signal }
    );
    return presentationSchema.nullable().parse(data);
  } catch (error) {
    handleApiError(error, 'load proposal details');
  }
}
export async function putProposalPresentation(
  project: string,
  proposal: string,
  body: {
    expectedProposalRevision: number;
    expectedPresentationRevision: number;
    milestones: MilestonePresentation[];
  }
) {
  try {
    const payload = z
      .object({
        expectedProposalRevision: revision,
        expectedPresentationRevision: revision,
        milestones: z.array(milestonePresentationSchema).max(100),
      })
      .strict()
      .parse(body);
    const { data } = await adapterClient.put<unknown>(
      path(project, proposal) + '/presentation',
      payload
    );
    return presentationSchema.parse(data);
  } catch (error) {
    handleApiError(error, 'save proposal details');
  }
}
export async function getProposalComments(
  project: string,
  proposal: string,
  after = 0,
  signal?: AbortSignal
) {
  try {
    const { data } = await adapterClient.get<unknown>(
      path(project, proposal) + '/comments',
      { params: { after }, signal }
    );
    return commentsSchema.parse(data);
  } catch (error) {
    handleApiError(error, 'load proposal responses');
  }
}
export async function postProposalComment(
  project: string,
  proposal: string,
  body: z.infer<typeof commentBodySchema>
) {
  try {
    const { data } = await adapterClient.post<unknown>(
      path(project, proposal) + '/comments',
      commentBodySchema.parse(body)
    );
    return commentSchema.parse(data);
  } catch (error) {
    handleApiError(error, 'save proposal response');
  }
}
export async function proposalRevision(project: string, proposal: string) {
  const view = await getProjectView(project);
  const current = view.proposals.find((item) => item.proposalId === proposal);
  if (!current) throw new Error('This proposal is no longer available.');
  return current.revision;
}
